import { Card } from "@mui/material";
import Fade from "@mui/material/Fade";
import moment from "moment";
import React, { useCallback, useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import GlobalContext from "../../framework/GlobalContext";
import { searchCategories } from "../../services/category-services";
import { fetchDiscountProgramDetails } from "../../services/discount-program";
import classes from "./DiscountProgram.module.css";
import DiscountProgramActors from "./DiscountProgramActors";
import {
  CategoryList,
  discountProgramColDef,
} from "./DiscountProgramConstants";
import { DiscountProgramContextProvider } from "./DiscountProgramContext";
import DiscountProgramGrid from "./DiscountProgramGrid";
import { createDiscountProgramItemFormData } from "./DiscountProgramUtils";
import { searchManufacturerGroup } from "../../services/manufacturer-group-services";
import { listEntity } from "../../services/entity-services";
import { fetchBranches } from "../../services/branch-service";

const DiscountProgram = () => {
  const { autocompleteOpen, findCategory } = useContext(GlobalContext);
  const activeTabIndex = useSelector(
    (state) => state.commonReducer.activeTabIndex
  );
  const { props } = useSelector(
    (state) => state.commonReducer.tabs[state.commonReducer.activeTabIndex]
  );

  const { manufacturerGroupOptions, entityOptions, allBranches } = useSelector(
    (state) =>
      state.commonReducer.tabs[state.commonReducer.activeTabIndex].actors
  );

  const { categoryOptions } = useSelector((state) => state.commonReducer);

  const dispatch = useDispatch();
  const getManufacturerGroupOptions = useCallback(async () => {
    const rows = await searchManufacturerGroup({
      page: 1,
      limit: 20,
    });

    const options = (rows || []).map((g) => ({
      id: g.manufacturerGroupId,
      label: g.name,
    }));
    dispatch({
      type: "update_discount_program_actors",
      payload: {
        manufacturerGroupOptions: options,
      },
    });

    return options;
  }, [dispatch]);


  const focusField = useCallback((id) => {
    document.getElementById(id)?.focus({ preventScroll: true });
  }, []);

 
  const initializeDiscountProgram = useCallback(() => {
    if (props?.replicateDiscountProgramData) {
      dispatch({
        type: "update_discount_program_actors",
        payload: {
          name: props?.replicateDiscountProgramData?.programName,
          start: moment(props?.replicateDiscountProgramData?.startDate).format(
            "DD/MM/YYYY"
          ),
          end: moment(props?.replicateDiscountProgramData?.endDate).format(
            "DD/MM/YYYY"
          ),
        },
      });
      let rowData = [];
      if (
        Array.isArray(
          props?.replicateDiscountProgramData?.discountProgramDetails
        )
      ) {
        props?.replicateDiscountProgramData.discountProgramDetails.forEach(
          (item) => {
            const { discountProgramDetailsId, ...rest } = item;
            let _category = findCategory(categoryOptions || [], item.typeId);
            rowData.push(
              createDiscountProgramItemFormData(
                {
                  ...rest,
                  option: {
                    value: _category,
                    categoryName: _category?.categoryName || "",
                    categoryId: item.categoryId,
                  },
                  category: CategoryList[0] || null,
                },
                true
              )
            );
          }
        );
      }
      dispatch({
        type: "update_discount_program_grid",
        payload: rowData,
      });
      return;
    }

    fetchDiscountProgramDetails(
      `discountProgramCode=${props?.discountProgramCode}`
    ).then(async (resp) => {
      const data = resp?.result?.rows || resp || {};
      const allEntities = await listEntity();
      const entities = allEntities?.result?.rows || [];
      const allBranchesOptions = await fetchBranches();
      const allBranches = allBranchesOptions?.result?.rows || [];
      let rowData = [];
      const details = Array.isArray(data?.discountProgramDetails)
        ? data.discountProgramDetails
        : [];
      const entity = entities?.find(
        (option) => option.entityId === details[0]?.entityId
      );
      const matchingBranches =
        allBranches?.filter((branch) => {
          if (branch?.code) {
            return data?.branchCode?.includes(branch.code);
          }
          if (Array.isArray(branch?.branchCode)) {
            return branch.branchCode.some((code) =>
              data?.branchCode?.includes(code)
            );
          }
          return false;
        }) || [];

      dispatch({
        type: "update_discount_program_actors",
        payload: {
          name: data?.programName,
          start: data?.startDate
            ? moment(data.startDate).format("DD/MM/YYYY")
            : null,
          end: data?.endDate ? moment(data.endDate).format("DD/MM/YYYY") : null,
          discountProgramCode:
            data?.discountProgramCode || props?.discountProgramCode || null,
          branch: matchingBranches,
          entity: entity || null,
          entityOptions: entities || [],
        },
      });

      details.forEach((item) => {
        const manufacturerGroup = manufacturerGroupOptions?.find(
          (option) => option.id === item.typeId
        ) || { id: item.typeId, label: item.typeName };

        rowData.push(
          createDiscountProgramItemFormData(
            {
              ...item,
              option: manufacturerGroup,
              category: CategoryList[0] || null,
            },
            true
          )
        );
      });
      dispatch({
        type: "update_discount_program_grid",
        payload: rowData,
      });
    });
    dispatch({ type: "update_props", payload: null });
  }, [
    props?.replicateDiscountProgramData,
    props?.discountProgramCode,
    dispatch,
    findCategory,
    categoryOptions,
    manufacturerGroupOptions,
  ]);

  const discountProgramListener = useCallback(
    (event) => {
      const key = event.key?.toLowerCase();
      const altKey = event.altKey;
      if (altKey) {
        if (altKey && key === "n") {
          focusField("discount_program_name");
          return;
        }
      }
    },
    [focusField]
  );

  useEffect(() => {
    getManufacturerGroupOptions();
  }, [
    getManufacturerGroupOptions,
  ]);

  useEffect(() => {
    searchCategories().then((response) => {
      if (response) {
        let updatedResp = [];
        response?.result?.rows?.forEach((cat) => {
          updatedResp.push({
            ...cat,
            id: cat.categoryId,
            name: cat.categoryName,
          });
        });
        dispatch({
          type: "update_category_options",
          payload: updatedResp,
        });
      }
    });
  }, [dispatch]);

  useEffect(() => {
    const dbData = localStorage.getItem("DiscountProgramGridColumns");
    if (!dbData) {
      localStorage.setItem(
        "DiscountProgramGridColumns",
        JSON.stringify({
          colId: 1,
          lastUpdated: new Date(),
          details: discountProgramColDef,
        })
      );
    }
    dispatch({
      type: "update_grid_columnDefs",
      payload: dbData ? JSON.parse(dbData).details : discountProgramColDef,
    });
  }, [dispatch, activeTabIndex]);

  useEffect(() => {
    if (props?.discountProgramCode || props?.replicateDiscountProgramData) {
      initializeDiscountProgram(props?.discountProgramCode);
    }
  }, [initializeDiscountProgram, props]);

  useEffect(() => {
    if (!autocompleteOpen)
      document.addEventListener("keyup", discountProgramListener, false);
    return () => {
      document.removeEventListener("keyup", discountProgramListener, false);
    };
  }, [autocompleteOpen, discountProgramListener]);

  return (
    <DiscountProgramContextProvider>
      <Fade in={true} timeout={500}>
        <Card
          sx={{
            width: "100%",
            height: "100%",
            padding: "5px",
            borderRadius: 0,
          }}
        >
          <div
            className={`${classes["discountProgram-medicine-bill-parent-wrapper"]}`}
          >
            <Card
              sx={{
                width: "100%",
                height: "100%",
                overflow: "visible",
                borderRadius: "4px",
              }}
            >
              <DiscountProgramGrid />
            </Card>
          </div>
          <Card
            sx={{
              width: "100%",
              height: "140px",
              border: 1,
              p: "0px 5px",
              transition: "500ms width ease-in",
              background: "#E0E0E0",
            }}
          >
            <DiscountProgramActors />
          </Card>
        </Card>
      </Fade>
    </DiscountProgramContextProvider>
  );
};
export default DiscountProgram;
